import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { type ColumnDef, DataTable } from '~/components/composites/data-table'
import { Input } from '~/components/ui/input'
import { useQuery } from '~/hooks/use-query'
import type { ReferralSummaryResponse, ReferralSummaryRow } from '~/types/referral'
import { QueryKey } from '~/types/query-key'

type ReferralRow = ReferralSummaryRow & { id: string }

export function ReferralsList() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery<ReferralSummaryResponse>({
    queryKey: QueryKey.ADMIN_REFERRALS_SUMMARY,
  })

  // The summary endpoint returns every referrer unpaginated, so filtering
  // client-side is enough. Row id maps to the referrer's user id, which the
  // detail route and DataTable key both need.
  const rows = useMemo<ReferralRow[]>(() => (data?.referrers ?? []).map((r) => ({ ...r, id: r.referrerId })), [data])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    if (!term) return rows
    return rows.filter((r) => r.username.toLowerCase().includes(term) || (r.heroId ?? '').toLowerCase().includes(term))
  }, [rows, search])

  const total = filtered.reduce((sum, r) => sum + r.referredCount, 0)

  const columns: ColumnDef<ReferralRow>[] = [
    {
      key: 'username',
      header: t('referrals.referrer'),
      render: (row) => <span className="font-medium">{row.username}</span>,
    },
    {
      key: 'heroId',
      header: t('referrals.heroId'),
      render: (row) => row.heroId ?? '-',
      className: 'w-[200px]',
    },
    {
      key: 'referredCount',
      header: t('referrals.count'),
      render: (row) => row.referredCount,
      className: 'w-[160px]',
    },
  ]

  return (
    <div className="space-y-4">
      <Input
        id="referrals-search"
        placeholder={t('referrals.searchPlaceholder')}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />
      <p className="text-sm text-muted-foreground">
        {t('referrals.summaryLine', {
          referrers: filtered.length,
          total,
        })}
      </p>
      <DataTable
        columns={columns}
        data={filtered}
        loading={isLoading}
        onRowClick={(row) => navigate(`/referrals/${row.id}`)}
      />
    </div>
  )
}
